import { useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import type { SidePanelRequest } from './panelPlacement'
import { useSidePlacement } from './popoverPlacement'

/**
 * THE PRODUCT'S HINT: a few words that appear beside a control while the
 * pointer rests on it or the focus stands on it, and go when either leaves.
 *
 * It exists because the browser's `title` is not one. That attribute waits
 * about a second, draws in the platform's own face rather than the product's,
 * and never shows for a keyboard at all — so a rail of glyphs relying on it
 * would tell a mouse the name of a destination late and tell a keyboard
 * nothing. This one is drawn by the product, at once, on hover AND on focus,
 * and it is portaled and fixed like every other floating panel here, so the
 * scrolling column the control stands in cannot clip it.
 *
 * IT SAYS NOTHING THE CONTROL DOES NOT ALREADY SAY. A hint repeats the
 * control's accessible name for the eye — it is not a second channel for
 * facts a screen reader would then miss — which is why it carries no
 * `aria-describedby` wiring: the name is already on the control, and
 * announcing it twice is the one thing a tooltip must not do.
 *
 * WHO OPENS IT IS THE CALLER. This component draws a hint beside a given
 * element; deciding which element — one control's own hover, or a whole rail
 * listening for whichever glyph the pointer reaches — is the caller's, and
 * the rail is the first of them. Rendering with a `null` anchor draws nothing.
 */
export function Tooltip({
  anchor,
  side,
  children,
}: {
  /** The control the hint stands beside, or `null` while there is none. */
  anchor: HTMLElement | null
  side: SidePanelRequest['side']
  children: ReactNode
}) {
  const panel = useRef<HTMLDivElement | null>(null)
  const placement = useSidePlacement({ open: anchor !== null, anchor, panel, side, gap: HINT_GAP })

  if (anchor === null) {
    return null
  }
  return createPortal(
    <div
      ref={panel}
      role="tooltip"
      className={placement ? 'tooltip' : 'tooltip tooltip--measuring'}
      style={{ left: placement?.left ?? 0, top: placement?.top ?? 0 }}
    >
      {children}
    </div>,
    document.body,
  )
}

/** Physical, like `VIEWPORT_MARGIN`: the clearance between a control and its hint. */
const HINT_GAP = 6

/**
 * HOW LONG A POINTER RESTS BEFORE A HINT APPEARS.
 *
 * Long enough that a pointer crossing a rail of glyphs, or a row of origin
 * chips, does not flash every name on the way; short enough that resting on one
 * is not a wait. Both callers held their own copy of the number with their own
 * half of that sentence (#467), which is how a hint delay comes to differ
 * between two surfaces of one product for no reason a reader could name.
 *
 * A FOCUS hint does not wait at all, and that is not this number being zero: a
 * keyboard reader asked for the control they landed on, so the caller shows the
 * hint at once and consults this only for the pointer.
 */
export const HINT_DELAY_MS = 120
